import classNames from "classnames";
import FocusTrap from "focus-trap-react";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import CrossIcon from "../../icons/CrossIcon";
import Button from "../Button/PlainButton/PlainButton";
import Text from "../Text/Text";
import { RemoveScroll } from "react-remove-scroll";
import styles from "./Modal.module.scss";
import { useString } from "../../i18n/GetText";
import { useModalManager } from "../../utils/useModalManager";

export interface ModalProps {
  id: string;
  children: ReactNode;
  isOpen: boolean;
  dismissible?: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  className?: string;
}

const Modal = ({
  id: modalId,
  children,
  className,
  isOpen,
  dismissible = true,
  onClose,
  title,
  subtitle
}: ModalProps) => {
  const [focusTrapActive, setFocusTrapActive] = useState(false);
  const { isTopModal, modalsOpen } = useModalManager();

  useEffect(() => {
    const gatsbyFocusWrapper = document.getElementById("gatsby-focus-wrapper");
    const cleanUp = () => {
      if (gatsbyFocusWrapper) {
        // Reset attribute.
        gatsbyFocusWrapper.setAttribute("tabIndex", "-1");
      }
    };
    if (isOpen) {
      setFocusTrapActive(true);
      if (gatsbyFocusWrapper) {
        // Remove tabIndex which causes text selection to fail while ReactFocusTrap is active.
        // See https://github.com/focus-trap/focus-trap/issues/870
        // See https://github.com/gatsbyjs/gatsby/issues/29037
        gatsbyFocusWrapper.removeAttribute("tabIndex");
      }
    } else {
      setFocusTrapActive(false);
      if (!modalsOpen) {
        cleanUp();
      }
    }
    return () => {
      if (!modalsOpen) {
        cleanUp();
      }
    };
  }, [isOpen, modalsOpen, onClose]);

  const keyListener = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && isTopModal(modalId)) {
        onClose();
      }
    },
    [isTopModal, modalId, onClose]
  );

  const modalContainerRef = useRef<HTMLDivElement>(null);
  const clickListener = useCallback(
    (e: MouseEvent) => {
      if (e.target === modalContainerRef.current && isTopModal(modalId)) {
        onClose();
      }
    },
    [isTopModal, modalId, onClose]
  );

  const addEventListeners = useCallback(() => {
    window.addEventListener("click", clickListener);
    window.addEventListener("keydown", keyListener);
  }, [clickListener, keyListener]);

  const removeEventListeners = useCallback(() => {
    window.removeEventListener("click", clickListener);
    window.removeEventListener("keydown", keyListener);
  }, [clickListener, keyListener]);

  useEffect(() => {
    if (isOpen) {
      if (dismissible) {
        addEventListeners();
      }
    } else {
      removeEventListeners();
    }
    return removeEventListeners;
  }, [addEventListeners, dismissible, isOpen, modalId, removeEventListeners]);

  const closeButtonText = useString("close");
  return (
    <div
      className={classNames(styles.root, isOpen ? styles.isOpen : undefined)}
    >
      <RemoveScroll
        as="div"
        enabled={focusTrapActive}
        className={styles.contentContainer}
        ref={modalContainerRef}
        gapMode="padding"
      >
        <FocusTrap
          active={focusTrapActive}
          focusTrapOptions={{
            escapeDeactivates: false,
            allowOutsideClick: true,
            preventScroll: true
          }}
        >
          <section
            className={classNames(styles.content, className)}
            aria-modal
            role="dialog"
            aria-labelledby={`dialog-label-${modalId}`}
          >
            {dismissible && (
              <Button
                className={styles.closeButton}
                onClick={onClose}
                aria-label={closeButtonText}
              >
                <CrossIcon />
              </Button>
            )}
            <div className={styles.header}>
              {title && (
                <Text id={`dialog-label-${modalId}`} as="h2" variant="h2">
                  {title}
                </Text>
              )}
              {subtitle && <Text variant="default">{subtitle}</Text>}
            </div>
            {children}
          </section>
        </FocusTrap>
      </RemoveScroll>
    </div>
  );
};

export default Modal;
